import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { DialogContentText } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useLocation } from "react-router-dom";
import { useIsAuthenticated } from "../auth/cognito-react";
import { redirectToLogoutPage } from "../utils/auth-functions";
import {
  saveBrandingColorsToSession,
  saveLogoutLogoToSession,
} from "../utils/brandingSession";
import { ConfiguratorContext } from "../../contexts/ConfiguratorContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import {
  FOOTER_COMPANY_NAME_KEY,
  LOGO_IMAGE_KEY,
  LOGOUT_HAS_MESSAGE_KEY,
  LOGOUT_MESSAGE_KEY,
  LOGOUT_SUBTITLE_KEY,
} from "../constants/Configurator.constants";
import { IDLE_TIMEOUT_MS } from "../constants/Idle.constants";
import { useIdleTimeout } from "../hooks/useIdleTimeout";
import { blobToBase64 } from "../utils/utility-functions";
import ConfirmationDialog from "./ConfirmationDialog";

const IdleSessionManager = () => {
  const theme = useTheme();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { addPendingRequest, removePendingRequest } = useContext(ThemeContext);
  const { edittedFields } = useContext(ConfiguratorContext);

  const [warningOpen, setWarningOpen] = useState(false);
  const loggingOutRef = useRef(false);

  const isOnLogoutPage = location.pathname === "/logout";
  const idleTrackingEnabled = isAuthenticated && !isOnLogoutPage && !warningOpen;

  const saveLogoutMarketingItems = useCallback(async () => {
    try {
      const logoField = edittedFields.find((item) => item.key === LOGO_IMAGE_KEY);
      if (logoField?.value) {
        const base64Logo = await blobToBase64(logoField.value);
        saveLogoutLogoToSession(base64Logo);
      }
    } catch (_) {
      /* ignore logo serialization errors */
    }
    sessionStorage.setItem(
      "_hm",
      edittedFields.find((item) => item.key === LOGOUT_HAS_MESSAGE_KEY)?.value ===
        "true",
    );
    sessionStorage.setItem(
      "_m",
      edittedFields.find((item) => item.key === LOGOUT_MESSAGE_KEY)?.value || "",
    );
    sessionStorage.setItem(
      "_s",
      edittedFields.find((item) => item.key === LOGOUT_SUBTITLE_KEY)?.value || "",
    );
    saveBrandingColorsToSession(theme.palette);
    sessionStorage.setItem(
      "_cn",
      edittedFields.find((item) => item.key === FOOTER_COMPANY_NAME_KEY)?.value ||
        "Mortgage Builder",
    );
  }, [edittedFields, theme.palette]);

  const performLogout = useCallback(async () => {
    if (loggingOutRef.current) {
      return;
    }
    loggingOutRef.current = true;
    setWarningOpen(false);
    addPendingRequest();
    await saveLogoutMarketingItems();
    removePendingRequest();
    redirectToLogoutPage();
  }, [addPendingRequest, removePendingRequest, saveLogoutMarketingItems]);

  const handleIdle = useCallback(() => {
    setWarningOpen(true);
  }, []);

  const { clearTimer } = useIdleTimeout({
    timeoutMs: IDLE_TIMEOUT_MS,
    onIdle: handleIdle,
    enabled: idleTrackingEnabled,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      setWarningOpen(false);
      clearTimer();
      loggingOutRef.current = false;
    }
  }, [isAuthenticated, clearTimer]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <ConfirmationDialog
      open={warningOpen}
      dismiss={() => {}}
      close={performLogout}
      title="Session timeout"
      hasComponent
      hasDismissButton={false}
      confirmButtonLabel="Sign in again"
      body={
        <DialogContentText id="idle-session-description" marginTop="10px">
          You have been inactive for 15 minutes. For your security, your session
          has ended. Please sign in again to continue.
        </DialogContentText>
      }
    />
  );
};

export default IdleSessionManager;
